import React, { useState } from 'react';
import styled from 'styled-components';
import { ChevronDown16Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import { Icon } from '@repo/design-system';
import { semantic } from '@repo/design-tokens';
import type { ModelDiff } from '@repo/front-end-utils';
import { isEmptyModelDiff } from '@repo/front-end-utils';

import LaunchDetail, { type LaunchDetailGroup } from './LaunchDetail';
import { BI_GAP_HEADINGS } from './launchSummaryDetail';
import ModelDiffView from './ModelDiffView';

/** A single line in the post-deploy confirmation. `warn` flags a "live, but…" caveat. */
export type DeploymentRowTone = 'ok' | 'warn';

/**
 * Each row tone's glyph and colour (`onboarding-status-colours-launch`, R6): done in the success
 * colour's text-safe ink, as the step list's tick; a caveat a problem with a way out, in peach. Every
 * caveat row offers one (link the feeds, retry BI, launch again), so all of them are problems.
 */
export const ROW_LOOK = {
  ok: { glyph: 'circle-check', variant: 'solid', colour: semantic.text.success },
  warn: {
    glyph: 'triangle-exclamation',
    variant: 'regular',
    colour: semantic.status.roles.problem.mark,
  },
} as const satisfies Record<DeploymentRowTone, unknown>;

export interface DeploymentRow {
  tone: DeploymentRowTone;
  /** The headline outcome, e.g. "12 pieces of equipment are live". */
  label: string;
  /** Optional caveat below the label, e.g. how many observers were skipped and why. */
  detail?: string;
  /**
   * A closed list under the row text, naming what the caveat counts. Only the BI row sets it
   * (`launch-bi-gap-preview`, R3.4).
   */
  detailGroups?: { id: string; label: string; groups: readonly LaunchDetailGroup[] };
}

export interface DeploymentSummary {
  title: string;
  intro: string;
  rows: DeploymentRow[];
}

export interface BuildDeploymentSummaryParams {
  /** UPDATE flips the copy from "live" to "updated"; the actions behind it are identical. */
  updating: boolean;
  /** Entities in the activated tree — what is now live. */
  entitiesLive: number;
  /** Entities the activation removed from the previous live model (0 on a first activation). */
  entitiesRemoved: number;
  /**
   * Observer outcome, or null when the twin has no TwinSense config at all (no observers drawn and
   * none previously live) — in which case the observer/device rows are omitted entirely.
   */
  observers: { created: number; skipped: number } | null;
  /** Edge-device frame-config push outcome. Null when observers is null (nothing was pushed). */
  devices: { sent: number; total: number } | null;
  /** What happened to BI's hierarchies (`twin-launch-history-bi-push` R6). */
  bi: BiSummary;
  /**
   * The activated model's entity names by BID, to name the items BI did not fully match. An item
   * missing from it is named by its BID. Optional: absent names every item by its BID.
   */
  assetNameByBid?: ReadonlyMap<string, string>;
  /**
   * Set when filing found nothing changed and kept the live configuration (spec
   * twin-launch-history-record). The observer row then says so, instead of "N observers deployed"
   * for a version that was never written. Optional: absent reads as a minted version.
   */
  reused?: { versionNumber: number } | null;
  /**
   * Whether the launch reached the launch history. Null or absent when it was not attempted. A
   * launch that could not be recorded gets a warn row: the twin is live, but it is missing from the
   * history, and its claim keeps Go live held for up to five minutes from the launch's start. When
   * the answer never came, the record may have landed, so the row says both.
   */
  launchRecord?: { recorded: true } | { recorded: false; reason: string; unknown: boolean } | null;
  /**
   * Whether the launch history now says what BI did (spec twin-launch-history-bi-outcome). Null or
   * absent when it was not attempted — no launch record to attach it to. A save that failed, or got
   * no answer, adds one warn row; the BI row above it is still true.
   */
  biOutcomeSave?: BiOutcomeSave | null;
}

/** What saving the BI outcome to the launch history came to. */
export type BiOutcomeSave = 'saved' | 'not-saved' | 'unknown';

/**
 * BI's part of a launch, for the summary.
 *
 * - `updated` — both hierarchy updates ran. `linked` of `placed` items reached BI with a catalogue
 *   reference; `validationIssues` is how many of those references BI's catalogue did not match, or
 *   `null` when that could not be checked. Only the difference is linked to a manufacturer.
 * - `not-updated` — the person chose *Continue without BI update*; `reasonText` says why.
 */
export type BiSummary =
  | {
      kind: 'updated';
      entitiesImported: number;
      /**
       * Items sent to BI with a catalogue reference — not all of them necessarily matched. `null`
       * when there was nothing placed, so the manufacturer update was skipped.
       */
      linked: number | null;
      placed: number;
      validationIssues: number | null;
      /**
       * The items behind `validationIssues`, one per item (`launch-bi-gap-preview`, R3.1). `null`
       * exactly when `validationIssues` is. Optional: a summary built without it reports the count
       * in one sentence, as before.
       */
      issues?: readonly BiIssue[] | null;
    }
  | {
      kind: 'not-updated';
      reasonText: string;
      /** BI never answered: the update may still have landed, so the row must not say it did not. */
      uncertain?: boolean;
    };

/** One item BI's catalogue did not fully match, as the manufacturer update reported it. */
export interface BiIssue {
  entityBid: string;
  referenceId: string;
  /** `NO_MATCH_MANUFACTURER` or `NO_MATCH_EQUIP_TYPE`; anything else is read as a manufacturer miss. */
  issue: string;
}

const plural = (n: number, one: string, many = `${one}s`): string => (n === 1 ? one : many);

/**
 * Turns the raw Go Live outcomes into the display-ready confirmation shown in the success modal.
 * Pure and copy-only so the wording is unit-testable without rendering the dialog.
 *
 * Every row is a thing that genuinely happened: the model is live, observers were saved, the frame
 * config reached the device(s). A row goes `warn` (not `ok`) when the twin is live but something the
 * user drew did not fully make it — skipped observers, or a device the push could not reach — so the
 * caveat is visible rather than buried.
 */
export const buildDeploymentSummary = ({
  updating,
  entitiesLive,
  entitiesRemoved,
  observers,
  devices,
  bi,
  assetNameByBid,
  reused = null,
  launchRecord = null,
  biOutcomeSave = null,
}: BuildDeploymentSummaryParams): DeploymentSummary => {
  const rows: DeploymentRow[] = [];

  rows.push({
    tone: 'ok',
    label: `${entitiesLive} ${plural(entitiesLive, 'entity', 'entities')} ${
      updating ? 'updated and live' : 'now live'
    }`,
    detail:
      entitiesRemoved > 0
        ? `${entitiesRemoved} previous ${plural(entitiesRemoved, 'entity', 'entities')} removed`
        : undefined,
  });

  if (observers) {
    const { created, skipped } = observers;
    rows.push({
      tone: skipped > 0 ? 'warn' : 'ok',
      // No "replacing N previous observer(s)" clause: Go Live files a new configuration version
      // and never edits the one that was running, so that number could only ever be zero
      // (`camera-observer-setup-golive-config`).
      label: reused
        ? `Configuration unchanged — v${reused.versionNumber} kept, ${created} ${plural(created, 'observer')} measuring`
        : `${created} ${plural(created, 'observer')} deployed`,
      detail:
        skipped > 0
          ? `${skipped} drawn ${plural(skipped, 'observer')} skipped — each needs a completed ` +
            'polygon, a linked camera feed, and a target that exists on the plan.'
          : undefined,
    });

    if (devices) {
      if (devices.total === 0) {
        rows.push({
          tone: 'ok',
          label:
            'No edge device connected yet — the frame configuration will be sent when one comes online',
        });
      } else {
        rows.push({
          tone: devices.sent < devices.total ? 'warn' : 'ok',
          label: `Frame configuration sent to ${devices.sent} of ${devices.total} edge ${plural(
            devices.total,
            'device',
          )}`,
          detail:
            devices.sent < devices.total
              ? 'The rest could not be reached — re-send from admin if a device does not pick up the observers.'
              : undefined,
        });
      }
    }
  }

  rows.push(biRow(bi, assetNameByBid));

  // One problem, one row: a launch the history lacks altogether is said by the row below, and its
  // BI result could not have been attached to anything.
  // A BI result the history took proves the launch is recorded: the save writes only a completed
  // launch row. So a record whose answer was lost, followed by a saved BI result, is not reported
  // as possibly unrecorded.
  const launchUnrecorded =
    !!launchRecord &&
    'reason' in launchRecord &&
    !(launchRecord.unknown && biOutcomeSave === 'saved');
  if ((biOutcomeSave === 'not-saved' || biOutcomeSave === 'unknown') && !launchUnrecorded) {
    rows.push({
      tone: 'warn',
      label:
        biOutcomeSave === 'unknown'
          ? 'Launch history: BI result may not have been saved'
          : 'Launch history: BI result not saved',
      detail: 'The twin is live and BI is as shown above, but the launch history will not say so.',
    });
  }

  if (launchRecord && launchUnrecorded) {
    rows.push({
      tone: 'warn',
      label: launchRecord.unknown
        ? 'Launch history: this launch may not have been recorded'
        : 'Launch history: not recorded',
      // The server's reasons are sentences and end in a full stop; never print two.
      detail: `${launchRecord.reason.trim().replace(/[.\s]+$/, '')}. ${
        launchRecord.unknown
          ? 'If it was recorded, Go live is locked for 60 seconds; if not, it stays held for up to 5 minutes from when this launch started.'
          : 'Go live stays held for up to 5 minutes from when this launch started.'
      }`,
    });
  }

  return {
    title: updating ? 'Twin updated' : 'Twin is live',
    intro: updating
      ? "Here's what was deployed. You can refine any step and update again."
      : "Here's what was deployed. You can refine any step and go live again.",
    rows,
  };
};

/**
 * Why a BI update that ran is not clean — each a sentence for the summary's BI row. Empty means
 * clean. ⚠️ THE ONE PLACE THIS RULE LIVES: the BI row turns amber on it, and the launch history
 * records `updated_with_gaps` on it (`toBiOutcome`), so the two can never disagree (spec
 * twin-launch-history-bi-outcome, R1.4 and R4.1).
 */
export const biCaveats = (bi: Extract<BiSummary, { kind: 'updated' }>): string[] => {
  const { linked, placed, validationIssues, issues } = bi;
  const caveats: string[] = [];
  if (linked !== null && linked < placed) {
    const unlinked = placed - linked;
    caveats.push(
      `${unlinked} ${plural(unlinked, 'item has', 'items have')} no catalogue match in the asset register.`,
    );
  }
  if (validationIssues === null) {
    caveats.push('Manufacturer matches could not be checked.');
  } else if (issues) {
    // Split by kind (`launch-bi-gap-preview`, R3.2). Every issue lands in one of the two sentences,
    // so the caveats are non-empty exactly when they were before the split.
    const { manufacturer, equipmentType } = splitIssues(issues);
    if (manufacturer.length > 0) {
      caveats.push(
        `${manufacturer.length} ${plural(manufacturer.length, 'reference is', 'references are')} not in BI's manufacturer catalogue.`,
      );
    }
    if (equipmentType.length > 0) {
      caveats.push(
        `${equipmentType.length} ${plural(equipmentType.length, 'reference has', 'references have')} no equipment type in BI.`,
      );
    }
  } else if (validationIssues > 0) {
    caveats.push(
      `${validationIssues} ${plural(validationIssues, 'reference', 'references')} did not match BI's manufacturer catalogue.`,
    );
  }
  return caveats;
};

/**
 * The two kinds of miss. `NO_MATCH_EQUIP_TYPE` found the manufacturer but no equipment type, so the
 * item IS linked to a manufacturer. Any other kind is read as a manufacturer miss, as every issue
 * was before the split, so an unknown kind can never raise the matched figure.
 */
const splitIssues = (issues: readonly BiIssue[]) => ({
  manufacturer: issues.filter(i => i.issue !== 'NO_MATCH_EQUIP_TYPE'),
  equipmentType: issues.filter(i => i.issue === 'NO_MATCH_EQUIP_TYPE'),
});

/**
 * The BI row. `warn` whenever BI holds less than was launched: items with no catalogue reference,
 * references BI's catalogue could not match, a match check that could not run — or no update at all.
 */
const biRow = (bi: BiSummary, assetNameByBid?: ReadonlyMap<string, string>): DeploymentRow => {
  if (bi.kind === 'not-updated') {
    return {
      tone: 'warn',
      label: bi.uncertain ? 'BI may not have been updated' : 'BI was not updated',
      detail: `${bi.reasonText} The next launch will update it.`,
    };
  }

  const { entitiesImported, linked, placed, validationIssues, issues } = bi;
  const caveats = biCaveats(bi);
  const kinds = issues ? splitIssues(issues) : null;

  // A reference BI's catalogue did not match was sent but links to nothing, so it is not counted as
  // linked. When the match could not be checked, all that is known is how many references were sent.
  let manufacturerFact: string;
  if (linked === null || placed === 0) {
    manufacturerFact = ' No equipment placed, so there was nothing to link to a manufacturer.';
  } else if (validationIssues === null) {
    manufacturerFact = ` ${linked} of ${placed} ${plural(placed, 'item')} sent to BI with a catalogue reference.`;
  } else {
    // An equipment-type miss is still linked to a manufacturer (R3.3).
    const matched = Math.max(0, linked - (kinds ? kinds.manufacturer.length : validationIssues));
    manufacturerFact = ` ${matched} of ${placed} ${plural(placed, 'item')} linked to a manufacturer.`;
  }
  const facts =
    `Spatial hierarchy: ${entitiesImported} ${plural(entitiesImported, 'entity', 'entities')}.` +
    manufacturerFact;

  const row: DeploymentRow = {
    tone: caveats.length > 0 ? 'warn' : 'ok',
    label: 'BI updated',
    detail: [facts, ...caveats].join(' '),
  };
  if (issues?.length && kinds) {
    const toItem = (i: BiIssue) => ({
      id: i.entityBid,
      name: assetNameByBid?.get(i.entityBid) || i.entityBid,
      detail: i.referenceId,
    });
    row.detailGroups = {
      id: 'launch-success-bi-issues',
      label: `${issues.length} ${plural(issues.length, 'item')} BI did not fully match`,
      groups: [
        { heading: BI_GAP_HEADINGS.notInManufacturer, items: kinds.manufacturer.map(toItem) },
        { heading: BI_GAP_HEADINGS.noEquipmentType, items: kinds.equipmentType.map(toItem) },
      ],
    };
  }
  return row;
};

interface Props {
  summary: DeploymentSummary;
  /**
   * Field-level diff of the previously-live model against the new one, or null when there is nothing
   * to compare (a first activation / CREATE mode). When present and non-empty, a "View changes"
   * toggle reveals it inline. An empty diff (nothing changed) shows no toggle.
   */
  diff?: ModelDiff | null;
  /**
   * Turns the summary into the before/after model comparison. Passed only in UPDATE mode (there is a
   * prior model to compare against); its presence is what renders the "Compare models" action.
   */
  onCompareModels?: () => void;
  /**
   * The *Compare models* control, so the onboarding Modal can hand focus back to it on the way back
   * from the comparison (`onboarding-persistent-scene-launch-modals`, R6).
   */
  compareRef?: React.Ref<HTMLButtonElement>;
  className?: string;
}

/** The body of the Go Live success modal: an intro line and one ticked row per deployed thing. */
const LaunchSuccessSummary: React.FC<Props> = ({
  summary,
  diff,
  onCompareModels,
  compareRef,
  className,
}) => {
  const [showChanges, setShowChanges] = useState(false);
  const hasDiff = !!diff && !isEmptyModelDiff(diff);

  return (
    <div className={className}>
      <p className="intro">{summary.intro}</p>
      <ul className="rows">
        {summary.rows.map((row, i) => (
          <li key={i} className={`row row--${row.tone}`}>
            <span className="icon" aria-hidden>
              <Icon
                name={ROW_LOOK[row.tone].glyph}
                variant={ROW_LOOK[row.tone].variant}
                size="lg"
              />
            </span>
            <span className="text">
              <span className="label">{row.label}</span>
              {row.detail ? <span className="detail">{row.detail}</span> : null}
              {row.detailGroups ? <LaunchDetail {...row.detailGroups} /> : null}
            </span>
          </li>
        ))}
      </ul>

      {(hasDiff || onCompareModels) && (
        <div className="changes">
          <div className="changes-actions">
            {hasDiff && (
              <button
                type="button"
                className="link-btn"
                aria-expanded={showChanges}
                onClick={() => setShowChanges(v => !v)}
              >
                {showChanges ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                {showChanges ? 'Hide changes' : 'View changes'}
              </button>
            )}
            {onCompareModels && (
              <button type="button" className="link-btn" ref={compareRef} onClick={onCompareModels}>
                Compare models (before / after)
              </button>
            )}
          </div>
          {hasDiff && showChanges && <ModelDiffView diff={diff!} />}
        </div>
      )}
    </div>
  );
};

export default styled(LaunchSuccessSummary)`
  .intro {
    margin: 0 0 1rem;
    font-size: 0.9375rem;
    line-height: 1.5;
    color: #424242;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .row {
    display: flex;
    gap: 0.6rem;
    align-items: flex-start;
  }

  .icon {
    display: inline-flex;
    flex-shrink: 0;
    margin-top: 0.05rem;
  }

  .row--ok .icon {
    color: ${ROW_LOOK.ok.colour};
  }

  .row--warn .icon {
    color: ${ROW_LOOK.warn.colour};
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .label {
    font-size: 0.9375rem;
    line-height: 1.4;
    color: #1a1a1a;
  }

  .detail {
    font-size: 0.8125rem;
    line-height: 1.45;
    color: #6b6b6b;
  }

  .changes {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #ececec;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .changes-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
  }

  .link-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    color: #00a3b3;

    &:hover {
      color: #007f8c;
    }

    svg {
      color: inherit;
    }
  }
`;
